import React, {useEffect, useState} from "react";
import {DataGrid} from "@mui/x-data-grid";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import api from "API/api";
import { useParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import Icon from "@mui/material/Icon";
import colors from "assets/theme/base/colors";
import {toast} from "sonner";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import { S3SignedUrl } from "../../components/Common/s3Bucket";
import VideoPreviewModal from "../../components/Modals/VideoPreviewModal/VideoPreviewModal";
import { ImageCell } from "../../components/Common/imageCell";  


const AnalyticsReport = () => {
  const {token} = useParams();
  const {white, gradients} = colors;
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = user.role === "admin";
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [reportData, setReportData] = useState([]);
  const [downloadTimer, setDownloadTimer] = useState(null);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState("");
  const [selectedLocation, setSelectedLocation] = useState(
    isAdmin ? "" : user.site_id
  );
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [siteData, setSiteData] = useState([]);
  const [fullReportData, setFullReportData] = useState([]);
  const [selectedViolation, setSelectedViolation] = useState("");
  const [violationTypes, setViolationTypes] = useState([]);
  const [selectedServiceType, setSelectedServiceType] = useState("");
  const [serviceTypes, setServiceTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [violationsData, setViolationsData] = useState([]);

  localStorage.removeItem("reportUrl");
  const formatDate = (date) => {
    const d = new Date(date);
    const offset = d.getTimezoneOffset();
    d.setMinutes(d.getMinutes() - offset);
    return d.toISOString().split("T")[0];
  };

  const BUCKET = "benchmark-violation-videos";
  const getReport = async (filters = {}) => {
    
    try {
      setReportLoading(true);
      const queryParams = new URLSearchParams(filters).toString();
      const res = await api.get(`/analytics/report/${token}?${queryParams}`);
      const responseData = res?.data?.data?.violations || [];
      const sites = responseData.map((item) => item.site);
      setSiteData([...new Set(sites)]);

      let serviceTypeSet = new Set();
      let violationTypeSet = new Set();
      let tableData = [];

      responseData.forEach((siteItem) => {
        siteItem.violations.forEach((service) => {
          serviceTypeSet.add(service.service_type);
          service.violations.forEach((violationItem) => {
            violationTypeSet.add(violationItem.violation);
            violationItem.records.forEach((record) => {
              tableData.push({
                id: record.id,
                site: siteItem.site,
                service_type: service.service_type,
                violation: record.violation || "-",
                description: record.description || "-",
                date: record.created_at || "-",
                image: record.violation_image,
                clip: record.violation_clip,
              });
            });
          });
        });
      });

      setViolationsData(responseData);
      setViolationTypes([...violationTypeSet]);
      setServiceTypes([...serviceTypeSet]);
      setFullReportData(tableData);
      setReportData(tableData);
    } catch (error) {
      toast.error(error?.response?.data?.message);
    } finally {
      setReportLoading(false);
    }
  };

  const populatetable = async (filters = {}) => {
    try {
      let serviceTypeSet = new Set();
      let violationTypeSet = new Set();
      let tableData = [];

      violationsData.forEach((siteItem) => {
        siteItem.violations.forEach((service) => {
          serviceTypeSet.add(service.service_type);
          service.violations.forEach((violationItem) => {
            violationTypeSet.add(violationItem.violation);
            violationItem.records.forEach((record) => {
              if (
                (!filters.site ||
                  (filters.site && filters.site === siteItem.site)) &&
                (!filters.violation ||
                  (filters.violation && filters.violation === record.violation))
              ) {
                tableData.push({
                  id: record.id,
                  site: siteItem.site,
                  service_type: service.service_type,
                  violation: record.violation || "-",
                  description: record.description || "-",
                  date: record.created_at || "-",
                  image: record.violation_image,
                  clip: record.violation_clip,
                });
              }
            });
          });
        });
      });

      setViolationTypes([...violationTypeSet]);
      setFullReportData(tableData);
      setReportData(tableData);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    const applyFilters = () => {
      let filtered = [...fullReportData];

      if (selectedLocation) {
        filtered = filtered.filter((item) => item.site === selectedLocation);
        if (filtered.length === 0) {
          toast.error(`No data found for Site: "${selectedLocation}"`);
        }
      }

      if (selectedServiceType) {
        filtered = filtered.filter(
          (item) =>
            item.service_type?.toLowerCase().trim() ===
            selectedServiceType.toLowerCase().trim()
        );
        if (filtered.length === 0) {
          toast.error(
            `No data found for Service Type: "${selectedServiceType}"`
          );
        }
      }

      if (selectedViolation) {
        filtered = filtered.filter(
          (item) => item.violation === selectedViolation
        );
        if (filtered.length === 0) {
          toast.error(`No data found for Violation: "${selectedViolation}"`);
        }
      }

      setReportData(filtered);
    };

    applyFilters();
  }, [fullReportData]);

  useEffect(() => {
    getReport();
  }, []);

  useEffect(() => {
    populatetable();
  }, [selectedViolation, selectedLocation]);

  const downloadVideo = (videoUrl, site) => {
    if (!videoUrl) return;

    fetch(videoUrl)
      .then((response) => response.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = url;
        a.download = `violation-${new Date().toLocaleDateString()}-${site}.mp4`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => toast.error(err?.response?.data?.message))
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchAndDownloadVideo = async (url, site) => {
    try {
      setLoading(true);
      const key = url.replace("s3://benchmark-violation-videos/", "");
      const videoUrl = await S3SignedUrl(BUCKET, key);
      const timer = setTimeout(() => downloadVideo(videoUrl, site), 100);
      setDownloadTimer(timer);
    } catch (error) {
      toast.error(error?.response?.data?.message);
    }
  };
  const handleDownload = (url, site) => {
    setSelectedVideoUrl(url);
    clearTimeout(downloadTimer);
    fetchAndDownloadVideo(url, site);
  };

  const exportToExcel = () => {
    if (!violationsData || violationsData.length === 0) return;

    const excelRows = [];

    violationsData.forEach((item) => {
      if (Array.isArray(item.violations)) {
        item.violations.forEach((service) => {
          if (Array.isArray(service.violations)) {
            service.violations.forEach(({violation, records}) => {
              if (Array.isArray(records)) {
                records.forEach((record) => {
                  excelRows.push({
                    "Violation Type": violation,
                    Area: record.area || "-",
                    Timestamp: formatDate(record.created_at),
                    Description: record.description || "-",
                  });
                });
              }
            });
          }
        });
      }
    });

    const ws = XLSX.utils.json_to_sheet(excelRows);

    const headerCells = ["A1", "B1", "C1", "D1"];
    headerCells.forEach((cell) => {
      if (!ws[cell]) return;
      ws[cell].s = {
        fill: {
          patternType: "solid",
          fgColor: {rgb: "063863"},
        },
        font: {
          bold: true,
          color: {rgb: "FFFFFF"},
        },
        alignment: {
          horizontal: "center",
          vertical: "center",
        },
      };
    });

    const wb = {
      Sheets: {Violations: ws},
      SheetNames: ["Violations"],
    };

    const excelBuffer = XLSX.write(wb, {
      bookType: "xlsx",
      type: "array",
      cellStyles: true,
    });

    const data = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

    FileSaver.saveAs(data, "violations.xlsx");
  };

  const columns = [
    {field: "description", headerName: "Description", width: 400},
    {field: "violation", headerName: "Violation", width: 250},
    {field: "date", headerName: "Date", width: 300},
    {
      field: "image",
      headerName: "Image",
      width: 300,
      renderCell: (params) =>
        params.row.image ? (
          <ImageCell src={params.row.image} bucket={BUCKET} />
        ) : (
          "No Image Available"
        ),
    },
    {
      field: "clip",
      headerName: "Download",
      width: 280,
      renderCell: (params) => (
        <Button
          variant="contained"
          onClick={() => {
            handleDownload(params?.formattedValue, params?.row?.site);
          }}
          sx={{
            width: "140px",
            height: "38px",
            fontSize: "14px",
            fontWeight: "500",
            background: `${gradients?.info?.main}`,
            color: white.main,
            border: "none",
            marginRight: "5px",
            marginBottom: "0",
            "&:hover": {
              background: `${gradients?.info?.state} !important`,
              color: white.main,
            },
          }}
        >
          {loading && selectedVideoUrl === params.value ? (
            <CircularProgress size={20} sx={{color: "white !important"}}/>
          ) : (
            <>
              <Icon>download</Icon> Stream
            </>
          )}
        </Button>
      ),
    },
    {
      field: "video",
      headerName: "Preview",
      width: 150,
      renderCell: (params) => (
        <IconButton
          sx={{ fontSize: "30px" }}
          onClick={() => {
            setPreviewModalOpen(true);
            setSelectedVideoUrl(params?.row?.clip);
          }}
        >
          <Icon sx={{ color: "info.focus" }}>preview</Icon>
        </IconButton>
      ),
    },
  ];
  const NoRowsOverlay = () => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        width: "100%",
        fontSize: "20px",
        fontWeight: 500,
        color: "#666",
        opacity: "0.4",
      }}
    >
      No Data Found
    </Box>
  );

  return (
    <DashboardLayout>
      <Box mt={6}>
        <h2 style={{ marginBottom: "50px" }}>Analytics Report</h2>
        <Box sx={{ display: "flex", justifyContent: "end" }}>
          <FormControl>
            <InputLabel>Select Site</InputLabel>
            <Select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              label="Select Site"
              sx={{background: "white", marginRight: "8px", width: "200px"}}
              className="react-select"
            >
              <MenuItem value="">All</MenuItem>
              {siteData.map((site, idx) => (
                <MenuItem key={idx} value={site}>
                  {site}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl>
            <InputLabel>Select Violation</InputLabel>
            <Select
              value={selectedViolation}
              onChange={(e) => setSelectedViolation(e.target.value)}
              label="Select Violation"
              sx={{background: "white", marginRight: "8px", width: "200px"}}
              className="react-select"
            >
              <MenuItem value="">All</MenuItem>
              {violationTypes.map((type, idx) => (
                <MenuItem key={idx} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            sx={{
              border: "none",
              background: "#063863 !important",
              color: "white !important",
              height: "42px",
              width: "180px",
              borderRadius: "6px",
              marginBottom: "28px",
              marginLeft: "7px",
              "&:hover": {
                background: "#063863 !important",
                color: "white !important",
              },
            }}
            onClick={exportToExcel}
          >
            Export CSV
          </Button>
        </Box>

        <DataGrid
          maxColumns={6}
          // getRowHeight={() => 60}
          loading={reportLoading}
          rowCount={reportData?.length}
          rowLength={reportData?.length}
          disableRowSelectionOnClick
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          slots={{
            noRowsOverlay: NoRowsOverlay,
          }}
          paginationModel={{ page, pageSize }}
          pageSizeOptions={[10, 20, 30, 40, 50, 100]}
          rows={reportData}
          columns={columns}
          disableColumnMenu
          disableColumnSelector
          sx={{
            marginTop: 3,
            boxShadow: 1,
            borderRadius: "8px",
            "& .MuiDataGrid-overlayWrapperInner": {background: "white"},
            "& .MuiCircularProgress-root": {color: `${gradients.info.main}`},
            "& .MuiDataGrid-columnHeaderTitle": {color: "#fff", fontWeight: '500'},
            "& .MuiDataGrid-cell": {outlineOffset: "none !important", outline: "none !important"},
            "& .MuiDataGrid-columnHeader": {background: "#063863", "& .MuiSvgIcon-root": {color: "#fff !important"}},
            "& .MuiDataGrid-row": {
              "&:nth-of-type(odd)": {backgroundColor: "#f8f9fa"},
              "&:hover": {backgroundColor: "rgb(25 108 215 / 0.02) !important"},
            },
          }}
        />
        {previewModalOpen && (
          <VideoPreviewModal
            handleOpen={previewModalOpen}
            handleClose={() => setPreviewModalOpen(false)}
            videoUrl={selectedVideoUrl}
          />
        )}
      </Box>
    </DashboardLayout>
  );
};

export default AnalyticsReport;
